/// <reference types="google.maps" />
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  type ComponentProps,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Platform, TextInput } from "react-native";

import {
  Button,
  Input,
  type InputKeyPressEvent,
  LoadingSpinner,
  Paragraph,
  Text,
  View,
  YStack,
} from "@/components";
import { US_STATES } from "@/settings/constants";
import { normalizeZipCode } from "@/utils/formatting";

const GOOGLE_MAPS_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";

const VALID_STATE_CODES = new Set(US_STATES.map((state) => state.value));

// safeguard for future non-web platforms, for which this won't work
const SUPPORTS_AUTOCOMPLETE =
  Platform.OS === "web" && Boolean(GOOGLE_MAPS_API_KEY);

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 250;
const BLUR_CLOSE_MS = 150;

const LISTBOX_ID = "address-suggestion-listbox";
const optionId = (index: number) => `address-suggestion-${index}`;

export type ParsedAddress = {
  streetAddress: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export type AddressAutocompleteProps = {
  ref?: Ref<TextInput>;
  name?: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChangeText: (text: string) => void;
  errorText?: string;
  onSelectAddress: (address: ParsedAddress) => void;
  /** Layout props forwarded to the root container (e.g. grid sizing). */
  rootProps?: ComponentProps<typeof View>;
};

const findComponent = (
  components: google.maps.places.AddressComponent[] | undefined,
  type: string,
) => components?.find((component) => component.types.includes(type));

export const parseAddressComponents = (
  components: google.maps.places.AddressComponent[] | undefined,
  fallbackStreet: string,
): ParsedAddress => {
  const streetNumber =
    findComponent(components, "street_number")?.longText ?? "";
  const route = findComponent(components, "route")?.longText ?? "";
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ").trim();
  const streetAddress2 = findComponent(components, "subpremise")?.longText;

  const city =
    findComponent(components, "locality")?.longText ??
    findComponent(components, "postal_town")?.longText ??
    findComponent(components, "sublocality")?.longText ??
    findComponent(components, "sublocality_level_1")?.longText ??
    undefined;

  const stateCode = findComponent(
    components,
    "administrative_area_level_1",
  )?.shortText;
  const state =
    stateCode && VALID_STATE_CODES.has(stateCode) ? stateCode : undefined;

  // Clamp to the leading 5 digits so a ZIP+4 never trips the form's `^\d{5}$`
  // validation on a field the user never touched.
  const zipCode =
    normalizeZipCode(
      findComponent(components, "postal_code")?.longText ?? "",
    ) || undefined;

  return {
    streetAddress: streetAddress || fallbackStreet,
    ...(streetAddress2 ? { streetAddress2 } : {}),
    city: city || undefined,
    state,
    zipCode,
  };
};

const requestSuggestions = async (
  places: google.maps.PlacesLibrary,
  input: string,
  sessionToken: google.maps.places.AutocompleteSessionToken,
): Promise<google.maps.places.AutocompleteSuggestion[]> => {
  const baseRequest = { input, includedRegionCodes: ["us"], sessionToken };
  let response: { suggestions: google.maps.places.AutocompleteSuggestion[] };
  try {
    response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
      {
        ...baseRequest,
        includedPrimaryTypes: ["street_address", "premise", "subpremise"],
      },
    );
  } catch {
    response =
      await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        baseRequest,
      );
  }
  return response.suggestions.filter((s) => s.placePrediction != null);
};

type HighlightSegment = { text: string; matched: boolean; start: number };

// Split the full prediction text into matched / unmatched runs using the
// character offsets Google returns, so we can bold what the user typed.
const buildHighlightSegments = (
  formattable: google.maps.places.FormattableText,
): HighlightSegment[] => {
  const { text, matches } = formattable;
  if (!matches?.length) return [{ text, matched: false, start: 0 }];

  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const { startOffset, endOffset } of matches) {
    const start = Math.max(startOffset, cursor);
    if (endOffset <= start) continue;
    if (start > cursor) {
      segments.push({
        text: text.slice(cursor, start),
        matched: false,
        start: cursor,
      });
    }
    segments.push({ text: text.slice(start, endOffset), matched: true, start });
    cursor = endOffset;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), matched: false, start: cursor });
  }
  return segments;
};

const SuggestionRow = ({
  id,
  suggestion,
  active,
  onSelect,
  onHover,
}: {
  id: string;
  suggestion: google.maps.places.AutocompleteSuggestion;
  active: boolean;
  onSelect: (suggestion: google.maps.places.AutocompleteSuggestion) => void;
  onHover: () => void;
}) => {
  const prediction = suggestion.placePrediction;
  if (!prediction) return null;

  return (
    <Button.Wrapper
      id={id}
      role="option"
      aria-selected={active}
      onPress={() => onSelect(suggestion)}
      onHoverIn={onHover}
      dataTrackingId="address-autocomplete-suggestion"
      paddingVertical="$100"
      paddingHorizontal="$150"
      alignItems="flex-start"
      backgroundColor={active ? "$navSelected" : "$fillModal"}
      hoverStyle={{ backgroundColor: "$navSelected" }}
    >
      <Paragraph size="$sm" color="$textPrimary">
        {buildHighlightSegments(prediction.text).map((segment) => (
          <Text
            key={segment.start}
            size="$sm"
            color="$textPrimary"
            whiteSpace="pre-wrap"
            bold={segment.matched}
          >
            {segment.text}
          </Text>
        ))}
      </Paragraph>
    </Button.Wrapper>
  );
};

const PlacesAutocomplete = ({
  label,
  name,
  placeholder,
  value,
  onChangeText,
  errorText,
  onSelectAddress,
  ref,
  rootProps,
}: AddressAutocompleteProps) => {
  const places = useMapsLibrary("places");

  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurRef.current) clearTimeout(blurRef.current);
    },
    [],
  );

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!places) return;
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new places.AutocompleteSessionToken();
      }

      const requestId = ++requestIdRef.current;
      try {
        const results = await requestSuggestions(
          places,
          input,
          sessionTokenRef.current,
        );
        // Replace the previous set only once new results arrive (issue #3),
        // and ignore responses that have been superseded by a newer query.
        if (requestId === requestIdRef.current) {
          setSuggestions(results);
          setActiveIndex(-1);
        }
      } catch {
        // Keep the previous results visible on a transient error.
      } finally {
        if (requestId === requestIdRef.current) {
          setHasSearched(true);
          setIsSearching(false);
        }
      }
    },
    [places],
  );

  const handleChangeText = useCallback(
    (text: string) => {
      onChangeText(text);
      setIsOpen(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);

      setActiveIndex(-1);
      if (!places || text.trim().length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setHasSearched(false);
        setIsSearching(false);
        return;
      }

      // Show the spinner but keep the current results visible until the next
      // set arrives (issues #2 and #3).
      setIsSearching(true);
      debounceRef.current = setTimeout(
        () => fetchSuggestions(text),
        DEBOUNCE_MS,
      );
    },
    [fetchSuggestions, onChangeText, places],
  );

  const handleSelect = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (blurRef.current) clearTimeout(blurRef.current);
      setIsOpen(false);
      setSuggestions([]);
      setHasSearched(false);
      setActiveIndex(-1);

      const prediction = suggestion.placePrediction;
      if (!prediction) return;
      const fallbackStreet = prediction.mainText?.text ?? prediction.text.text;

      try {
        const place = prediction.toPlace();
        await place.fetchFields({ fields: ["addressComponents"] });
        onSelectAddress(
          parseAddressComponents(place.addressComponents, fallbackStreet),
        );
      } catch {
        // If details fail, at least keep the street line the user selected.
        onSelectAddress({ streetAddress: fallbackStreet });
      } finally {
        // Start a fresh billing session for the next search.
        sessionTokenRef.current = places
          ? new places.AutocompleteSessionToken()
          : null;
      }
    },
    [onSelectAddress, places],
  );

  const handleFocus = useCallback(() => {
    if (blurRef.current) clearTimeout(blurRef.current);
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    if (blurRef.current) clearTimeout(blurRef.current);
    blurRef.current = setTimeout(() => {
      setIsOpen(false);
      setActiveIndex(-1);
    }, BLUR_CLOSE_MS);
  }, []);

  // Only surface the empty state after a query has actually completed
  // (issue #1) — never before we've hit the endpoint.
  const showEmptyState =
    hasSearched &&
    !isSearching &&
    suggestions.length === 0 &&
    value.trim().length >= MIN_QUERY_LENGTH;
  const showDropdown = isOpen && (suggestions.length > 0 || showEmptyState);

  return (
    <View position="relative" zIndex={isOpen ? 1000 : undefined} {...rootProps}>
      <Input.Controlled
        ref={ref}
        label={label}
        name={name}
        placeholder={placeholder}
        value={value}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        // Drive suggestion selection from the keyboard while focus stays in the
        // input (aria-activedescendant combobox pattern). Space is intentionally
        // left alone — addresses contain spaces, so only Enter commits a highlight.
        onKeyPress={(e: InputKeyPressEvent) => {
          const { key } = e.nativeEvent;
          if (key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
            return;
          }
          if (!isOpen || suggestions.length === 0) return;

          if (key === "ArrowDown") {
            e.preventDefault?.();
            setActiveIndex((index) => (index + 1) % suggestions.length);
          } else if (key === "ArrowUp") {
            e.preventDefault?.();
            setActiveIndex((index) =>
              index <= 0 ? suggestions.length - 1 : index - 1,
            );
          } else if (key === "Enter" && activeIndex >= 0) {
            e.preventDefault?.();
            handleSelect(suggestions[activeIndex]);
          }
        }}
        errorText={errorText}
        autoComplete="off"
        textContentType="streetAddressLine1"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? LISTBOX_ID : undefined}
        aria-activedescendant={
          activeIndex >= 0 ? optionId(activeIndex) : undefined
        }
        postfix={isSearching ? <LoadingSpinner size="$200" /> : undefined}
      />
      {showDropdown ? (
        <YStack
          id={LISTBOX_ID}
          // @ts-expect-error RN's Role type omits the web-only "listbox" role
          role="listbox"
          position="absolute"
          top="100%"
          left={0}
          right={0}
          marginTop="$50"
          zIndex={1000}
          maxHeight={280}
          overflow="hidden"
          elevation="$100"
          backgroundColor="$fillModal"
          borderWidth="$strokeWidth"
          borderColor="$strokeNeutral"
          borderRadius="$150"
        >
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, index) => (
              <SuggestionRow
                key={suggestion.placePrediction?.placeId}
                id={optionId(index)}
                suggestion={suggestion}
                active={index === activeIndex}
                onSelect={handleSelect}
                onHover={() => setActiveIndex(index)}
              />
            ))
          ) : (
            <Paragraph size="$sm" color="$textSecondary" padding="$150">
              No matching addresses
            </Paragraph>
          )}
        </YStack>
      ) : null}
    </View>
  );
};

/**
 * Address field with Google Places autocomplete. On web it suggests addresses
 * as the user types and calls {@link AddressAutocompleteProps.onSelectAddress}
 * with parsed components on selection. Everywhere else it degrades to a plain
 * text input.
 */
export const AddressAutocomplete = ({
  ref,
  ...props
}: AddressAutocompleteProps) => {
  if (!SUPPORTS_AUTOCOMPLETE) {
    return (
      <Input.Controlled
        ref={ref}
        label={props.label}
        name={props.name}
        autoComplete="address-line1"
        textContentType="streetAddressLine1"
        value={props.value}
        onChangeText={props.onChangeText}
        placeholder={props.placeholder}
        errorText={props.errorText}
        rootProps={props.rootProps}
      />
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} disableUsageAttribution>
      <PlacesAutocomplete ref={ref} {...props} />
    </APIProvider>
  );
};
